import { Link, Outlet } from 'react-router';
import { useTranslation } from 'react-i18next';
import { consentEnabled, reopenConsent } from '@/lib/consent';
import { useCaptureReferralCode } from '@/lib/referral';
import { ConsentBanner } from '@/components/ConsentBanner';
import { LanguageSwitch } from '@/components/LanguageSwitch';

/** Pages with the app header (profile, confirmation, verify, 404). */
export function Layout() {
  const { t } = useTranslation();
  useCaptureReferralCode();
  return (
    <div className="min-h-dvh flex flex-col">
      <ConsentBanner />
      <header className="sticky top-0 z-10 border-b border-line bg-paper/90 backdrop-blur">
        <div className="mx-auto flex max-w-md items-center justify-between px-5 py-3">
          <Link
            to="/"
            className="font-display text-sm font-bold uppercase tracking-[0.12em] text-ink no-underline hover:text-ink"
          >
            {t('app.name')}
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/me" className="font-semibold text-ink-muted no-underline hover:text-ink">
              {t('nav.account')}
            </Link>
            <LanguageSwitch />
          </nav>
        </div>
      </header>
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col px-5 py-6 safe-bottom">
        <Outlet />
      </main>
      {consentEnabled && (
        <footer className="mx-auto w-full max-w-md px-5 pb-6 text-center">
          <button
            type="button"
            onClick={reopenConsent}
            className="text-xs text-ink-muted underline hover:text-ink"
          >
            {t('consent.manage')}
          </button>
        </footer>
      )}
    </div>
  );
}

/**
 * Full-screen pages without header or navigation: the checkout screens 01–03 (PRD §5) and
 * the login screen, which carry their own title. Centred column on desktop.
 * The checkout screens show the FR/EN switch top right (PRD §4.1 step 2, decided on #27);
 * login has its own at the bottom (screen 05).
 */
export function BareLayout({ languageSwitch = false }: { languageSwitch?: boolean }) {
  // Invite links land on screen 01 (`/formats?ref=…`).
  useCaptureReferralCode();
  // The consent banner sits above the page, in normal flow: most visitors land on screen 01.
  return (
    <>
      <ConsentBanner />
      <main className="relative mx-auto flex min-h-dvh w-full max-w-md flex-col px-5">
        {languageSwitch && (
          <div className="absolute top-4 right-5">
            <LanguageSwitch />
          </div>
        )}
        <Outlet />
      </main>
    </>
  );
}
